import { FileUp, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useDeleteJDs, useJDs, type JDSummary } from "@/api/jd-queries";
import { useCan } from "@/auth/member";
import { JDRow, Processing } from "@/components/jd-list-rows";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Empty, ErrorNote, Panel } from "@/components/ui/panel";
import { Segmented } from "@/components/ui/segmented";
import { SkeletonTableRow } from "@/components/ui/skeleton";

/**
 * Job descriptions — DESIGN §20.11, redesigned 2026-10-15. Every position a Resource Manager has
 * uploaded, opening on the ones that need review, each saying what it needs next. Uploads still being
 * read, or that failed, wait in a strip of their own above the list rather than as rows of a table they
 * do not belong to yet; the page polls while one is being read.
 */

const TH =
  "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase whitespace-nowrap";
type Status = "" | JDSummary["status"];

export default function JobDescriptions() {
  const canManage = useCan("manage_jd");
  const canPurge = useCan("purge_jd");
  const jds = useJDs();
  const purge = useDeleteJDs();
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [doomed, setDoomed] = useState<JDSummary[] | null>(null);
  const [params] = useSearchParams();
  // `?status=all` is how the upload page's "already uploaded" note opens the whole list.
  const asked = params.get("status");
  const [picked, setPicked] = useState<Status | null>(
    asked === "all"
      ? ""
      : ((["in_review", "active", "archived", "discarded"] as const).find((s) => s === asked) ?? null),
  );
  const [search, setSearch] = useState("");

  const items = useMemo(() => jds.data?.items ?? [], [jds.data]);
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const jd of items) out[jd.status] = (out[jd.status] ?? 0) + 1;
    return out;
  }, [items]);
  // Opens on the JDs that need somebody, unless there are none; a click on any tab takes over.
  const status: Status = picked ?? (counts.in_review ? "in_review" : "");

  const q = search.trim().toLowerCase();
  const shown = items.filter(
    (jd) =>
      (!status || jd.status === status) &&
      (!q ||
        [jd.title, jd.client_name ?? "", jd.source_filename ?? "", ...jd.stacks].some((v) =>
          v.toLowerCase().includes(q),
        )),
  );
  const pending = jds.data?.sources ?? [];
  // A published JD cannot be deleted, so it cannot be ticked; ticks follow what is on screen.
  const deletable = shown.filter((jd) => jd.status !== "active");
  const ticked = deletable.filter((jd) => checked.has(jd.id));
  const allTicked = deletable.length > 0 && ticked.length === deletable.length;
  const toggle = (id: number) =>
    setChecked((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <div className="flex min-h-full flex-col gap-3.5 p-4">
      <header className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <div className="min-w-0 flex-[1_1_360px]">
          <h1 className="text-base font-semibold tracking-tight">Job descriptions</h1>
          <p className="text-bank-muted text-[12.5px]">
            Upload a client's JD, check what the AI read, then publish it so members can practise for that
            role.
          </p>
        </div>
        <label className="relative block w-72 max-w-full">
          <Search className="text-bank-muted absolute top-2 left-2.5 size-4" aria-hidden="true" />
          <Input
            type="search"
            aria-label="Search job descriptions"
            placeholder="Search title, client, file or stack"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        {canManage && (
          <Link
            to="/jds/new"
            className="bg-bank-accent-strong text-bank-on-accent hover:bg-bank-accent-hover inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium"
          >
            <FileUp className="size-4" aria-hidden="true" />
            Upload JD
          </Link>
        )}
      </header>

      <Processing sources={pending} canManage={canManage} />

      <div className="flex flex-wrap items-center gap-2.5">
        <Segmented<Status>
          label="Filter by status"
          value={status}
          onChange={setPicked}
          options={[
            { value: "in_review", label: "Needs review", count: counts.in_review ?? 0, urgent: true },
            { value: "active", label: "Live", count: counts.active ?? 0 },
            { value: "archived", label: "Archived", count: counts.archived ?? 0 },
            { value: "discarded", label: "Discarded", count: counts.discarded ?? 0 },
            { value: "", label: "All", count: items.length },
          ]}
        />
        <span className="text-bank-muted ml-auto text-[12.5px]">
          Only Live JDs appear on ipp-platform's start form.
        </span>
      </div>

      <ErrorNote error={jds.error} />

      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bank-surface-2 border-bank-border border-b text-left">
              <tr>
                {canPurge && (
                  <th className="w-9 px-3 py-2">
                    <input
                      type="checkbox"
                      className="accent-bank-accent"
                      aria-label="Select every deletable job description shown"
                      checked={allTicked}
                      disabled={deletable.length === 0}
                      onChange={() =>
                        setChecked(allTicked ? new Set() : new Set(deletable.map((jd) => jd.id)))
                      }
                    />
                  </th>
                )}
                <th className={TH}>Position</th>
                <th className={TH}>Status</th>
                <th className={TH}>Next step</th>
                <th className={TH}>Skills covered</th>
                <th className={TH}>Questions</th>
                <th className={TH}>Experience</th>
                <th className={TH}>Updated</th>
                <th className="w-11" />
              </tr>
            </thead>
            <tbody>
              {jds.isLoading &&
                Array.from({ length: 4 }, (_, i) => (
                  <SkeletonTableRow
                    key={i}
                    columns={[
                      ...(canPurge ? ["w-4"] : []),
                      "w-48",
                      "w-16",
                      "w-28",
                      "w-28",
                      "w-24",
                      "w-12",
                      "w-20",
                      "w-6",
                    ]}
                  />
                ))}
              {shown.map((jd) => (
                <JDRow
                  key={jd.id}
                  jd={jd}
                  canManage={canManage}
                  canPurge={canPurge}
                  checked={checked.has(jd.id)}
                  onCheck={() => toggle(jd.id)}
                  onDelete={() => setDoomed([jd])}
                />
              ))}
            </tbody>
          </table>
        </div>
        {!jds.isLoading && shown.length === 0 && (
          <Empty>
            {items.length === 0
              ? "No job descriptions yet. Upload one, or paste the text a client sent."
              : q
                ? "Nothing matches that search."
                : "Nothing here."}
          </Empty>
        )}
      </Panel>

      {canPurge && ticked.length > 0 && (
        <div className="border-bank-accent bg-bank-accent-wash sticky bottom-3 mt-auto flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-4 py-2 shadow-lg">
          <span className="text-bank-accent-ink font-mono text-xs font-semibold tabular-nums">
            {ticked.length} selected
          </span>
          <span className="text-bank-muted text-[12.5px]">
            Live JDs can't be ticked — archive them first.
          </span>
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="danger" onClick={() => setDoomed(ticked)}>
              Delete permanently…
            </Button>
            <Button size="sm" onClick={() => setChecked(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={doomed !== null}
        title={
          doomed?.length === 1
            ? `Delete “${doomed[0]?.title ?? ""}” permanently?`
            : `Delete ${doomed?.length ?? 0} job descriptions permanently?`
        }
        confirmLabel="Delete permanently"
        busy={purge.isPending}
        error={purge.error}
        onCancel={() => setDoomed(null)}
        onConfirm={() =>
          purge.mutate(
            (doomed ?? []).map((jd) => jd.id),
            {
              onSuccess: () => {
                setChecked(new Set());
                setDoomed(null);
              },
            },
          )
        }
      >
        Their skills, question links and history are removed, and so is any uploaded document no other
        position is read from. This cannot be undone. Questions already in the bank stay.
      </ConfirmDialog>
    </div>
  );
}
